import { type useIntl } from 'react-intl'

import { type SupportConversationItem } from '@/components/organisms/SupportConversation'
import { type SupportRequestRow } from '@/components/organisms/SupportRequestList'
import { type SupportRequestDocument, type SupportRequestItemDocument } from '@/state/support/types'

import { fullName } from './supportAgentItems'
import { type useSupportLabels } from './useSupportLabels'

type Intl = ReturnType<typeof useIntl>

const requestActivity = (
  { requesterStatus, settledAt, lastActivity }: SupportRequestDocument,
  { unread, intl, labels, now }: { unread: boolean; intl: Intl; labels: ReturnType<typeof useSupportLabels>; now: Date }
) => {
  if (requesterStatus === 'settled' && settledAt) {
    return intl.formatMessage({ id: 'soutien.requests.activity.settled' }, { when: labels.relative(settledAt, now) })
  }
  const when = labels.relative(lastActivity.at, now)
  if (lastActivity.kind === 'agentReply') {
    return intl.formatMessage(
      { id: unread ? 'soutien.requests.activity.newReply' : 'soutien.requests.activity.reply' },
      { when }
    )
  }
  return intl.formatMessage({ id: 'soutien.requests.activity.sent' }, { when })
}

export const requestRow = (
  request: SupportRequestDocument,
  {
    uid,
    unread,
    intl,
    labels,
    now,
  }: { uid: string; unread: boolean; intl: Intl; labels: ReturnType<typeof useSupportLabels>; now: Date }
): SupportRequestRow => {
  const author =
    request.author.uid === uid ? intl.formatMessage({ id: 'soutien.requests.author.self' }) : fullName(request.author)
  return {
    number: request.number,
    meta: `#${request.number} · ${labels.category(request.category)} · ${author}`,
    title: request.title,
    requesterStatus: request.requesterStatus,
    blocking: request.blocking,
    activity: requestActivity(request, { unread, intl, labels, now }),
    unread,
  }
}

export const requesterThreadItem = (
  item: SupportRequestItemDocument,
  { uid, readUpTo, intl }: { uid: string; readUpTo: Date | null; intl: Intl }
): SupportConversationItem | null => {
  const time = intl.formatTime(item.createdAt)
  if (item.kind === 'message') {
    if (item.from === 'escouade') {
      const isNew = !readUpTo || item.createdAt > readUpTo
      return {
        id: item._id,
        kind: 'message',
        time,
        author: intl.formatMessage({ id: 'soutien.escouadeSignature' }, { firstName: item.author.firstName }),
        newLabel: isNew ? intl.formatMessage({ id: 'soutien.conversation.new' }) : undefined,
        text: item.text,
      }
    }
    return {
      id: item._id,
      kind: 'message',
      time,
      author: item.author.uid === uid ? intl.formatMessage({ id: 'soutien.conversation.self' }) : fullName(item.author),
      detail: item.opening ? intl.formatMessage({ id: 'soutien.conversation.opening' }) : undefined,
      text: item.text,
    }
  }
  if (item.kind !== 'event' || item.audience !== 'requester') {
    return null
  }
  const text = (() => {
    switch (item.type) {
      case 'taken':
        return intl.formatMessage({ id: 'soutien.event.taken' }, { firstName: item.assignee?.firstName ?? '' })
      case 'settled':
        return item.actor?.role === 'requester'
          ? intl.formatMessage({ id: 'soutien.event.settled.requester' }, { name: fullName(item.actor) })
          : intl.formatMessage({ id: 'soutien.event.settled.escouade' }, { firstName: item.actor?.firstName ?? '' })
      default:
        return intl.formatMessage({ id: `soutien.event.${item.type}` })
    }
  })()
  return { id: item._id, kind: 'event', time, icon: item.type === 'taken' ? 'taken' : 'status', text }
}

export const requesterThread = (
  items: ReadonlyArray<SupportRequestItemDocument>,
  options: { uid: string; readUpTo: Date | null; intl: Intl }
): SupportConversationItem[] =>
  [...items]
    .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime())
    .map((item) => requesterThreadItem(item, options))
    .filter((item): item is SupportConversationItem => item !== null)
